import {
  DEFAULT_STATE,
  LEGACY_STORAGE_KEY,
  MAX_PRESETS,
  MAX_PRESET_STORAGE_LENGTH,
  MAX_SHARE_STATE_LENGTH,
  STORAGE_KEY,
  TEMPLATES,
  VERSION_TWO_STORAGE_KEY
} from "../constants";
import type {
  MotifId,
  PaletteId,
  LegacyStudioState,
  SavedPreset,
  StudioState,
  TemplateId,
  VersionTwoStudioState
} from "../types";
import {
  legacyStudioStateSchema,
  savedPresetListSchema,
  savedPresetSchema,
  studioStateSchema,
  versionTwoStudioStateSchema
} from "../types";
import { createRandom, nextSeed, randomBetween, randomItem } from "./random";

function encodeBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function decodeBase64Url(value: string): string {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
}

function migrateVersionTwoState(state: VersionTwoStudioState): StudioState {
  return studioStateSchema.parse({
    ...state,
    version: 3,
    typography: {
      displayFont: "staatliches",
      bodyFont: "lato"
    }
  });
}

function migrateLegacyState(state: LegacyStudioState): StudioState {
  return migrateVersionTwoState(versionTwoStudioStateSchema.parse({
    version: 2,
    seed: state.seed,
    output: state.output,
    template: state.template,
    text: state.text,
    style: {
      ...state.style,
      motif: state.style.motif === "signal-field" ? "light-columns" : state.style.motif
    },
    animation: {
      duration: state.animation.duration,
      fps: state.animation.fps,
      colorDrift: state.animation.colorDrift,
      bokehDrift: state.animation.bokehDrift,
      shapeDrift: state.animation.shapeDrift
    }
  }));
}

export function parseStudioState(value: unknown): StudioState | null {
  const current = studioStateSchema.safeParse(value);
  if (current.success) return current.data;
  const versionTwo = versionTwoStudioStateSchema.safeParse(value);
  if (versionTwo.success) return migrateVersionTwoState(versionTwo.data);
  const legacy = legacyStudioStateSchema.safeParse(value);
  return legacy.success ? migrateLegacyState(legacy.data) : null;
}

export function serializeStudioState(state: StudioState): string {
  return encodeBase64Url(JSON.stringify(studioStateSchema.parse(state)));
}

export function deserializeStudioState(encoded: string): StudioState | null {
  if (!encoded || encoded.length > MAX_SHARE_STATE_LENGTH) return null;
  try {
    return parseStudioState(JSON.parse(decodeBase64Url(encoded)));
  } catch {
    return null;
  }
}

export function loadStateFromUrl(search = window.location.search): StudioState | null {
  const encoded = new URLSearchParams(search).get("state");
  return encoded ? deserializeStudioState(encoded) : null;
}

export function buildShareUrl(state: StudioState, base = window.location.href): string {
  const url = new URL(base);
  url.search = "";
  url.hash = "";
  url.searchParams.set("state", serializeStudioState(state));
  return url.toString();
}

export function loadPresets(storage: Storage = localStorage): SavedPreset[] {
  try {
    const currentRaw = storage.getItem(STORAGE_KEY);
    if (currentRaw && currentRaw.length <= MAX_PRESET_STORAGE_LENGTH) {
      const parsed = savedPresetListSchema.safeParse(JSON.parse(currentRaw));
      if (parsed.success) return parsed.data;
    }

    for (const key of [VERSION_TWO_STORAGE_KEY, LEGACY_STORAGE_KEY]) {
      const legacyRaw = storage.getItem(key);
      if (!legacyRaw || legacyRaw.length > MAX_PRESET_STORAGE_LENGTH) continue;
      const parsedLegacy = JSON.parse(legacyRaw);
      if (!Array.isArray(parsedLegacy)) continue;
      const migrated = parsedLegacy.slice(0, MAX_PRESETS).flatMap((preset) => {
        const metadata = {
          id: preset?.id,
          name: preset?.name,
          createdAt: preset?.createdAt
        };
        const state = parseStudioState(preset?.state);
        const parsed = savedPresetSchema.safeParse({ ...metadata, state });
        return parsed.success ? [parsed.data] : [];
      });
      if (migrated.length > 0) return migrated;
    }
    return [];
  } catch {
    return [];
  }
}

export function savePresets(presets: SavedPreset[], storage: Storage = localStorage): void {
  storage.setItem(
    STORAGE_KEY,
    JSON.stringify(savedPresetListSchema.parse(presets.slice(0, MAX_PRESETS)))
  );
}

export function createSavedPreset(name: string, state: StudioState): SavedPreset {
  return {
    id: `${Date.now().toString(36)}-${state.seed.toString(36)}`,
    name: name.trim().slice(0, 60),
    createdAt: new Date().toISOString(),
    state: structuredClone(state)
  };
}

export function applyTemplate(state: StudioState, templateId: TemplateId): StudioState {
  const template = TEMPLATES.find((candidate) => candidate.id === templateId);
  if (!template) return state;
  return {
    ...state,
    template: template.id,
    output: template.output,
    text: { ...state.text, ...template.layout }
  };
}

export function randomizeVisuals(
  state: StudioState,
  locks: { palette: boolean; motif: boolean }
): StudioState {
  const seed = nextSeed();
  const random = createRandom(seed);
  const palettes: PaletteId[] = ["noir", "ember", "violet", "cobalt", "bone"];
  const motifs: MotifId[] = ["mesh", "bokeh", "arches", "light-columns", "orbital-halo"];
  const palette = locks.palette ? state.style.palette : randomItem(random, palettes);
  const motif = locks.motif ? state.style.motif : randomItem(random, motifs);

  return {
    ...state,
    seed,
    style: {
      ...state.style,
      palette,
      motif,
      variance: randomBetween(random, 0.18, 0.68),
      vibrance: randomBetween(random, 0.68, 1.1),
      brightness: randomBetween(random, 0.82, 1.12),
      grain: randomBetween(random, 0.07, 0.22),
      bokeh: randomBetween(random, 0.18, 0.78),
      vignette: randomBetween(random, 0.12, 0.56)
    },
    animation: {
      ...state.animation,
      colorDrift: randomBetween(random, 0.2, 0.72),
      bokehDrift: randomBetween(random, 0.18, 0.64),
      shapeDrift: randomBetween(random, 0.12, 0.5)
    }
  };
}

export function createInitialState(search?: string): StudioState {
  const shared = typeof window === "undefined" ? null : loadStateFromUrl(search);
  return shared ?? structuredClone(DEFAULT_STATE);
}
